// theme/typography.ts
// Typography configuration based on Figma design system

import { TypographyOptions } from '@mui/material/styles/createTypography';

// Extend typography variants to include custom ones
declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: React.CSSProperties;
    monoSmall: React.CSSProperties;
    monoLarge: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    mono?: React.CSSProperties;
    monoSmall?: React.CSSProperties;
    monoLarge?: React.CSSProperties;
  }
}

// Update Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
    monoSmall: true;
    monoLarge: true;
  }
}

// Extended typography options with custom variants
export interface ExtendedTypographyOptions extends TypographyOptions {
  fontFamilyDisplay?: string;
  fontFamilyMono?: string;
  mono?: React.CSSProperties;
  monoSmall?: React.CSSProperties;
  monoLarge?: React.CSSProperties;
}

export const typography: ExtendedTypographyOptions = {
  fontFamily: '"Exo 2", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Display font for headers
  fontFamilyDisplay: '"Rajdhani", sans-serif',

  // Monospace font for data/numbers
  fontFamilyMono: '"Share Tech Mono", "JetBrains Mono", monospace',

  // Variants
  h1: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.2,
    letterSpacing: '0.02em',
  },

  h2: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: 1.2,
    letterSpacing: '0.02em',
  },

  h3: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 600,
    fontSize: '1.75rem',
    lineHeight: 1.3,
    letterSpacing: '0.02em',
  },

  h4: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.3,
    letterSpacing: '0.02em',
  },

  h5: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.4,
    letterSpacing: '0.02em',
  },

  h6: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.4,
    letterSpacing: '0.02em',
  },

  subtitle1: {
    fontFamily: '"Exo 2", sans-serif',
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.5,
  },

  subtitle2: {
    fontFamily: '"Exo 2", sans-serif',
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },

  body1: {
    fontFamily: '"Exo 2", sans-serif',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.5,
  },

  body2: {
    fontFamily: '"Exo 2", sans-serif',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },

  button: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.75,
    letterSpacing: '0.02em',
    textTransform: 'none' as const, // Don't uppercase buttons
  },

  caption: {
    fontFamily: '"Exo 2", sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },

  overline: {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 500,
    fontSize: '0.75rem',
    lineHeight: 2,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
  },

  // Custom variants
  mono: {
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.5,
  },

  monoSmall: {
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },

  monoLarge: {
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 400,
    fontSize: '1.5rem',
    lineHeight: 1.2,
  },
};

// Font imports for HTML head
export const fontImports = `
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Exo+2:wght@300;400;500;600;700&family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&display=swap" rel="stylesheet">
`;
